import React, { useState, useEffect } from 'react';
import { Youtube, Save, X, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = getBackendUrl() + '/api';

export const VideoSettings = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchCurrentVideo();
  }, []);

  const fetchCurrentVideo = async () => {
    try {
      setFetching(true);
      const response = await axios.get(`${API}/admin/manual-video`);
      if (response.data.enabled) {
        setCurrentVideo(response.data);
      } else {
        setCurrentVideo(null);
      }
    } catch (error) {
      console.error('Error fetching current video:', error);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!videoUrl.trim()) {
      toast.error('Please enter a YouTube URL');
      return;
    }
    
    if (!title.trim()) {
      toast.error('Please enter a video title');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('video_url', videoUrl);
      formData.append('title', title);
      if (description) {
        formData.append('description', description);
      }

      await axios.post(`${API}/admin/manual-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Video set successfully!');
      setVideoUrl('');
      setTitle('');
      setDescription('');
      await fetchCurrentVideo();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error(error.response?.data?.detail || 'Failed to save video');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear manual video and return to AI mode?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/admin/manual-video`);
      toast.success('Returned to AI video mode');
      setCurrentVideo(null);
    } catch (error) {
      console.error('Error clearing video:', error);
      toast.error('Failed to clear video');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="glass-card p-8 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="glass-card p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-br from-orange-500 to-red-500 p-3 rounded-xl">
            <Youtube className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Video of the Day Settings</h2>
            <p className="text-slate-400 text-sm">Set a custom YouTube video or use AI-generated content</p>
          </div>
        </div>

        {/* Current Status */}
        <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-300">Current Mode:</p>
              <p className="text-white font-bold">
                {currentVideo ? '📹 Manual Video (Admin Set)' : '🤖 AI-Generated Video'}
              </p>
            </div>
            {currentVideo && (
              <Button
                onClick={handleClear}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-red-600 text-red-400 hover:bg-red-900/30"
              >
                <X className="w-4 h-4 mr-1" />
                Clear & Return to AI
              </Button>
            )}
          </div>

          {currentVideo && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-slate-400 mb-2">Currently Showing:</p>
              <p className="text-white font-semibold">{currentVideo.title}</p>
              <p className="text-slate-400 text-xs mt-1">
                Set by {currentVideo.set_by} • {new Date(currentVideo.set_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
              </p>
              {currentVideo.embed_url && (
                <a
                  href={currentVideo.watch_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 hover:text-orange-300 text-xs mt-2 inline-block"
                >
                  View on YouTube →
                </a>
              )}
            </div>
          )}
        </div>

        {/* Info Alert */}
        <div className="mb-6 p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-blue-200 text-sm">
                <strong>How it works:</strong> When you set a manual video, it will be embedded and displayed instead of the AI-generated video. The manual video remains until you clear it.
              </p>
            </div>
          </div>
        </div>

        {/* Set Manual Video Form */}
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <Label htmlFor="videoUrl" className="text-white">
              YouTube Video URL <span className="text-red-400">*</span>
            </Label>
            <Input
              id="videoUrl"
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="mt-1"
              required
            />
            <p className="text-xs text-slate-400 mt-1">
              Paste any YouTube video link (watch URL, short URL, or embed URL)
            </p>
          </div>

          <div>
            <Label htmlFor="title" className="text-white">
              Video Title <span className="text-red-400">*</span>
            </Label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., How to Install Deck Joists"
              className="mt-1"
              required
            />
          </div>

          <div>
            <Label htmlFor="description" className="text-white">
              Description (Optional)
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the video..."
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Set Manual Video
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
